import React, { useRef } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import GridListTile from '@material-ui/core/GridListTile'

// Local replacement for react-storefront/ResponsiveTiles.
// Renders children in a responsive, wrapping flex grid whose column count
// is driven by the `cols` breakpoint map. Negative margin + per-tile padding
// produce an even gutter of theme.spacing(spacing).
//
// The original supported an `autoScrollToNewTiles` prop (default false);
// it is not used anywhere in this codebase and is intentionally omitted.

function createTiles({ cols, spacing }) {
  const useStyles = makeStyles(theme => {
    const breakpoints = {}
    // Breakpoints MUST be applied from smallest to largest column count.
    Object.keys(cols)
      .map(width => ({
        key: width,
        value: cols[width],
        width: `${100 / cols[width]}%`,
      }))
      .sort((a, b) => a.value - b.value)
      .forEach(({ key, width }) => {
        breakpoints[theme.breakpoints.up(key)] = { width }
      })

    return {
      root: {
        display: 'flex',
        flexWrap: 'wrap',
        overflowY: 'auto',
        listStyle: 'none',
        padding: 0,
        margin: `-${theme.spacing(spacing)}px`,
        WebkitOverflowScrolling: 'touch',
      },
      tile: {
        ...breakpoints,
        padding: `${theme.spacing(spacing)}px`,
        height: 'auto',
      },
    }
  }, 'RSFResponsiveTiles')

  return function Tiles({ className, classes, autoScrollToNewTiles, children, ...other }) {
    classes = useStyles({ classes })
    return (
      <ul className={clsx(className, classes.root)} {...other}>
        {React.Children.map(children, (child, i) => {
          if (!React.isValidElement(child)) {
            return null
          }
          return (
            <GridListTile key={i} classes={{ root: classes.tile }}>
              {child}
            </GridListTile>
          )
        })}
      </ul>
    )
  }
}

export default function ResponsiveTiles({
  autoScrollToNewTiles = false,
  cols = { xs: 2, sm: 3, md: 4, lg: 5, xl: 5 },
  spacing = 1,
  ...rest
}) {
  const props = { autoScrollToNewTiles, cols, spacing, ...rest }
  const Tiles = useRef(createTiles(props))
  const { cols: _c, spacing: _s, ...others } = props
  return <Tiles.current {...others} />
}
